import { type DataTableColumn, type DataTableDefaultColumnProps } from 'mantine-datatable'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import type { CoinItemInGetList, CoinItemInPostList } from '@/services/coin/coins'
import type { CoinSlug } from '@/types/coin'

import styles from '../styles.module.css'
import ActionColumn from './Action'
import CoinColumn from './Coin'
import MarketCapColumn from './MarketCap'
import PriceColumn from './Price'
import PriceChangeColumn from './PriceChange'
import PriceChartColumn from './PriceChart'
import VolumeColumn from './Volume'

export type TableRecord = CoinItemInGetList | CoinItemInPostList

export const defaultColumnProps: DataTableDefaultColumnProps<TableRecord> = {
  titleClassName: `typography-body ${styles.tableHeader}`,
  cellsClassName: `typography-body-number ${styles.tableCell}`,
}

export function useColumns(
  restrictedAreasMap: Map<CoinSlug, string[]>,
  isOGPrice?: boolean,
  coinColumnTitle?: string,
  isTokenizedStocks?: boolean,
  tokenizedStockSlugSet?: Set<string>,
  showRank = true,
) {
  const t = useTranslations('coinTable.headers')
  return useMemo<DataTableColumn<TableRecord>[]>(
    () =>
      (
        [
          showRank && {
            accessor: 'rank',
            title: '#',
            sortKey: 'rank',
            sortable: true,
            textAlign: 'left',
            width: 70,
            visibleMediaQuery: theme => `(min-width: ${theme.breakpoints.midScale})`,
          },
          {
            accessor: 'coin',
            title: coinColumnTitle ?? t('coin'),
            sortable: true,
            sortKey: 'name',
            ellipsis: true,
            cellsClassName: styles.defaultCell,
            render: coin => (
              <CoinColumn
                coin={coin}
                isOGPrice={isOGPrice}
                isTokenizedStocks={isTokenizedStocks || tokenizedStockSlugSet?.has(coin.slug)}
              />
            ),
          },
          {
            accessor: 'price',
            title: t('price'),
            sortable: true,
            sortKey: 'usd_price',
            textAlign: 'right',
            render: coin => <PriceColumn coin={coin} />,
          },
          {
            accessor: '24hChange',
            title: t('24hChange'),
            textAlign: 'right',
            sortable: true,
            sortKey: 'usd_price_change_24h',
            render: coin => <PriceChangeColumn coin={coin} />,
          },
          {
            accessor: '24hChart',
            title: t('24HChart'),
            textAlign: 'right',
            render: coin => <PriceChartColumn coinId={coin.token_id} />,
          },
          !isTokenizedStocks && {
            accessor: 'marketCap',
            title: t('marketCap'),
            textAlign: 'right',
            sortable: true,
            sortKey: 'usd_marketcap',
            render: coin => <MarketCapColumn coin={coin} />,
          },
          !isTokenizedStocks && {
            accessor: 'volume',
            title: t('24hVolume'),
            textAlign: 'right',
            sortable: true,
            sortKey: 'usd_volume_24h',
            render: coin => <VolumeColumn coin={coin} />,
          },
          {
            accessor: 'action',
            title: '',
            textAlign: 'center',
            // Constrain the actions column to be no wider than its content
            width: '0%',
            render: coin => (
              <ActionColumn
                isOGPrice={isOGPrice}
                restrictedAreas={restrictedAreasMap.get(coin.slug) ?? []}
              />
            ),
          },
        ] as DataTableColumn<TableRecord>[]
      ).filter(Boolean),
    [t, restrictedAreasMap, isOGPrice, coinColumnTitle, isTokenizedStocks, showRank],
  )
}
